import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Coins, ExternalLink, TrendingUp, TrendingDown, Loader2, AlertTriangle,
  MessageCircle, Hash, Globe, Twitter, Search, ArrowRight, BarChart3,
} from 'lucide-react';
import { getCoinDetail, getCoinCommunity, searchCoins } from '../services/api';
import useTokenPrice from '../hooks/useTokenPrice';
import TimeAgo from '../components/TimeAgo';

function formatNum(v, dp = 2) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

function formatCompact(v) {
  if (!v) return '—';
  const n = Number(v);
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(2)}K`;
  return `$${n.toFixed(2)}`;
}

export default function CoinDetailPage() {
  const { coinId } = useParams();
  const navigate = useNavigate();
  const { data: coin, loading, error } = useTokenPrice(coinId, 10000); // 10s polling, pauses when tab hidden
  const [community, setCommunity] = useState(null);

  // Search bar
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  // Surface search errors instead of pretending no matches were found
  const [searchError, setSearchError] = useState(null);

  // Community overlay — separate fetch (no polling, runs once per coin).
  useEffect(() => {
    if (!coinId) { setCommunity(null); return; }
    let cancelled = false;
    getCoinCommunity(coinId)
      .then(r => { if (!cancelled) setCommunity(r?.data || r || null); })
      .catch(() => { if (!cancelled) setCommunity(null); });
    return () => { cancelled = true; };
  }, [coinId]);

  // Debounced search
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      setSearchError(null);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      setSearchError(null);
      try {
        const res = await searchCoins(searchQuery);
        setSearchResults(res?.data?.coins || []);
      } catch (err) {
        setSearchResults([]);
        setSearchError(err?.message || 'Search failed — please try again');
      } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  if (!coinId) {
    // Search-only landing
    return (
      <div className="space-y-6 animate-fade-in max-w-3xl">
        <header>
          <div className="flex items-center gap-3 mb-2">
            <Coins className="w-7 h-7 text-amber-400" />
            <h1 className="text-2xl font-bold text-white">Coin Database</h1>
          </div>
          <p className="text-sm text-gray-400">Search any of 10,000+ coins. Real CoinGecko data with DexScreener fallback for on-chain tokens.</p>
        </header>

        <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} searchResults={searchResults} searching={searching} searchError={searchError} navigate={navigate} />
      </div>
    );
  }

  if (loading && !coin) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>;

  if (error || !coin) {
    // Helpful 404 — guide the user to the next sensible action instead of dead-ending.
    return (
      <div className="max-w-2xl space-y-4 animate-fade-in">
        <Link to="/coin" className="text-xs text-gray-400 hover:text-white">← Back to search</Link>
        <div className="bg-dark-800/50 border border-dark-700/50 rounded-2xl p-6">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white font-bold text-base">Token not found</p>
              <p className="text-xs text-gray-400 mt-1">
                We couldn't find <span className="text-amber-400 font-mono">{coinId}</span> in CoinGecko or DexScreener.
              </p>
            </div>
          </div>
          <div className="text-xs text-gray-400 space-y-1.5 ml-8">
            <p className="text-gray-300 font-medium">Try one of these:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Enter a contract address (<span className="font-mono">0x…</span>) instead of a symbol</li>
              <li>Check the spelling of the token name or symbol</li>
              <li>
                Search <a href={`https://dexscreener.com/search?q=${encodeURIComponent(coinId)}`} target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:underline">DexScreener</a> directly for this token
              </li>
            </ul>
          </div>
          <div className="flex gap-2 mt-5 ml-8">
            <Link to="/coin" className="text-xs bg-dark-700 hover:bg-dark-600 text-white px-3 py-1.5 rounded-lg transition-colors">
              Search a different coin
            </Link>
            <Link to="/swap?tab=discover" className="text-xs bg-amber-500 hover:bg-amber-400 text-black font-semibold px-3 py-1.5 rounded-lg transition-colors">
              Browse trending tokens
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const priceUp = coin.priceChange24h >= 0;
  const isDexScreener = coin.source === 'dexscreener';
  return (
    <div className="space-y-6 animate-fade-in max-w-5xl">
      <Link to="/coin" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1">← Back to search</Link>

      {/* DexScreener fallback banner — clear about where the data is from */}
      {isDexScreener && (
        <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-2xl p-4 flex items-start gap-3">
          <BarChart3 className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-cyan-300 font-medium">Data from DexScreener</p>
            <p className="text-xs text-gray-400 mt-0.5">
              This token isn't tracked by CoinGecko. Some fields (market cap rank, descriptions, ATH history) may be limited.
            </p>
            {coin.dexUrl && (
              <a href={coin.dexUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-2 text-xs text-cyan-400 hover:text-cyan-300">
                View full chart on DexScreener <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            {coin.image && <img src={coin.image} alt={coin.name} className="w-16 h-16 rounded-full" />}
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold text-white">{coin.name}</h2>
                <span className="text-sm text-gray-500 font-mono">{coin.symbol}</span>
                {coin.marketCapRank && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">
                    #{coin.marketCapRank}
                  </span>
                )}
              </div>
              <p className="text-3xl font-bold font-mono text-white mt-2">${formatNum(coin.currentPriceUsd, coin.currentPriceUsd >= 1 ? 4 : 8)}</p>
              <p className={`text-sm font-mono ${priceUp ? 'text-emerald-400' : 'text-red-400'} flex items-center gap-1`}>
                {priceUp ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                {priceUp ? '+' : ''}{formatNum(coin.priceChange24h, 2)}% (24h)
              </p>
              {/* Freshness pill — surfaces the backend's existing fetchedAt + source fields */}
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <TimeAgo timestamp={coin.fetchedAt} />
                {coin.fromCache && (
                  <span className="text-[10px] text-gray-500 uppercase tracking-wider">cached</span>
                )}
                {coin.source === 'stale' && (
                  <span className="text-[10px] text-amber-400 uppercase tracking-wider">⚠ Source temporarily unavailable</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Link to={`/smart-trade?symbol=${coin.symbol}/USDT`}
              className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold text-xs px-4 py-2 rounded-lg flex items-center gap-1">
              Trade {coin.symbol} <ArrowRight className="w-3 h-3" />
            </Link>
            <Link to={`/swap?symbol=${coin.symbol}`}
              className="text-xs text-gray-400 hover:text-white">
              DEX swap →
            </Link>
          </div>
        </div>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Market Cap" value={formatCompact(coin.marketCapUsd)} />
        <Stat label="24h Volume" value={formatCompact(coin.volume24hUsd)} />
        <Stat label="7d Change" value={`${coin.priceChange7d >= 0 ? '+' : ''}${formatNum(coin.priceChange7d, 2)}%`} accent={coin.priceChange7d >= 0 ? 'emerald' : 'red'} />
        <Stat label="30d Change" value={`${coin.priceChange30d >= 0 ? '+' : ''}${formatNum(coin.priceChange30d, 2)}%`} accent={coin.priceChange30d >= 0 ? 'emerald' : 'red'} />
        <Stat label="ATH" value={`$${formatNum(coin.athUsd, 2)}`} sub={coin.athDate ? new Date(coin.athDate).toLocaleDateString() : ''} />
        <Stat label="From ATH" value={`${formatNum(coin.athChangePct, 2)}%`} accent="red" />
        <Stat label="Circulating" value={formatNum((coin.circulatingSupply || 0) / 1e6, 2) + 'M'} sub={coin.symbol} />
        <Stat label="Max Supply" value={coin.maxSupply ? formatNum(coin.maxSupply / 1e6, 2) + 'M' : '∞'} />
      </div>

      {/* Description */}
      {coin.description && (
        <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-5">
          <h3 className="text-sm font-bold text-white mb-2">About {coin.name}</h3>
          <p className="text-xs text-gray-400 leading-relaxed" dangerouslySetInnerHTML={{ __html: coin.description.replace(/<a /g, '<a class="text-cyan-400 hover:underline" target="_blank" rel="noopener noreferrer" ') }} />
          <div className="flex items-center gap-3 mt-3 flex-wrap text-xs">
            {coin.homepageUrl && <a href={coin.homepageUrl} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white flex items-center gap-1"><Globe className="w-3 h-3" /> Website</a>}
            {coin.twitterHandle && <a href={`https://twitter.com/${coin.twitterHandle}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white flex items-center gap-1"><Twitter className="w-3 h-3" /> @{coin.twitterHandle}</a>}
            {coin.subredditUrl && <a href={coin.subredditUrl} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white flex items-center gap-1"><Hash className="w-3 h-3" /> Reddit</a>}
          </div>
        </div>
      )}

      {/* haythix community overlay */}
      {community && (
        <div className="bg-violet-500/5 border border-violet-500/20 rounded-2xl p-5">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-violet-400" /> haythix Community Signals
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Mentions (24h)" value={community.mentions24h} accent={community.mentions24h > 0 ? 'violet' : undefined} />
            <Stat label="Verified callers" value={community.verifiedCallerCount || '—'} sub="On this coin" />
            <Stat label="Avg accuracy" value={community.avgCallerAccuracy != null ? `${formatNum(community.avgCallerAccuracy, 1)}%` : '—'} />
          </div>
          <p className="text-[10px] text-gray-500 mt-3">Mentions counted from real chat messages in the last 24h. Verified caller stats coming once enough callers track this coin.</p>
        </div>
      )}
    </div>
  );
}

function SearchBar({ searchQuery, setSearchQuery, searchResults, searching, searchError, navigate }) {
  const hasQuery = (searchQuery || '').trim().length >= 2;
  return (
    <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-5">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search 10,000+ coins by name or symbol…"
          className="w-full bg-dark-700 border border-dark-600 rounded-lg pl-9 pr-4 py-3 text-white text-sm" autoFocus />
      </div>
      {searching && <p className="text-xs text-gray-500 mt-2 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> searching…</p>}

      {/* Search error — distinguish a failed request from "no matches" */}
      {!searching && searchError && hasQuery && (
        <div className="mt-3 bg-red-500/5 border border-red-500/20 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs">
            <p className="text-red-300 font-semibold">Search failed</p>
            <p className="text-red-300/80 mt-0.5">{searchError}</p>
          </div>
        </div>
      )}

      {/* Genuine empty result — only show after a search ran and returned nothing */}
      {!searching && !searchError && hasQuery && searchResults.length === 0 && (
        <p className="text-xs text-gray-500 mt-3">
          No matches for "<span className="font-mono text-gray-400">{searchQuery}</span>". Try a different spelling, or paste a contract address (0x…) for on-chain tokens.
        </p>
      )}

      {searchResults.length > 0 && (
        <div className="mt-3 divide-y divide-dark-700/50 max-h-96 overflow-y-auto">
          {searchResults.map(c => (
            <button key={c.id} onClick={() => navigate(`/coin/${c.id}`)}
              className="w-full text-left flex items-center gap-3 p-2.5 hover:bg-dark-700/30 rounded-lg">
              {c.thumb && <img src={c.thumb} alt={c.name} className="w-6 h-6 rounded-full" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{c.name}</p>
                <p className="text-[10px] text-gray-500 font-mono">{c.symbol}</p>
              </div>
              {c.marketCapRank && <span className="text-[10px] text-gray-500">#{c.marketCapRank}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub, accent }) {
  const colorMap = { emerald: 'text-emerald-400', red: 'text-red-400', violet: 'text-violet-400' };
  return (
    <div className="bg-dark-700/30 rounded-lg p-3">
      <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`text-base font-bold font-mono mt-0.5 ${colorMap[accent] || 'text-white'}`}>{value}</p>
      {sub && <p className="text-[10px] text-gray-600 mt-0.5">{sub}</p>}
    </div>
  );
}
